import { API_URL, cofactorAppHostSuffix, parseTenantAppHost } from '../config';

/** Query param that carries a target org id on deep links; consumed by AuthContext. */
export const ORGANIZATION_CONTEXT_QUERY = 'cofactor_org_id';

const TENANT_LABEL_PATTERN = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** True when `value` is a well-formed organization id (a UUID). */
export function isValidOrganizationId(value: string): boolean {
  return UUID_RE.test(value);
}

/**
 * Decide where switching to `targetOrgId` should land, given the host the
 * switcher is running on and whether the target org has its own vanity
 * subdomain (`targetAppSubdomain`).
 *
 * A tenant host (`<label>.app.<suffix>`) pins the session to one org, so
 * leaving a tenant host must always be a host change opened in a *new* tab,
 * never an in-place switch, regardless of whether the target org has a
 * subdomain of its own. When the target has no subdomain, the new tab goes to the shared
 * app host with the target org id carried in {@link ORGANIZATION_CONTEXT_QUERY}
 * so `AuthContext` can switch the session cookie to that org on arrival.
 *
 * Returns the URL to open in a new tab, or null when it is safe to switch in
 * place on the current tab (already on the shared host with no target
 * subdomain, or on a host with no per-org addressing at all — canary hosts,
 * localhost).
 */
export function resolveOrgSwitchUrl(
  hostname: string,
  protocol: string,
  targetAppSubdomain: string | null,
  targetOrgId: string
): string | null {
  const suffix = cofactorAppHostSuffix(hostname);
  if (!suffix) return null; // canary hosts, localhost: no per-org host to move to

  if (targetAppSubdomain) {
    return `${protocol}//${targetAppSubdomain}.${suffix}`;
  }

  if (!parseTenantAppHost(hostname)) return null; // already on the shared host: switch in place

  const url = new URL(`${protocol}//${suffix}`);
  url.searchParams.set(ORGANIZATION_CONTEXT_QUERY, targetOrgId);
  return url.toString();
}

/**
 * Where to go when the API says this host uses a label the org has retired.
 *
 * Rebuilt from the CURRENT location's own suffix plus the label the API
 * supplied — never from the response alone — so a bad or hostile value cannot
 * move the origin off our domain. Path, query and hash are preserved: the
 * point is that an old bookmark still lands where it meant to.
 *
 * Returns null when the current host has no per-org shape to swap a label
 * into, which is not a state the API should produce; failing closed leaves the
 * user where they are rather than on a guessed origin.
 */
export function movedHostUrl(
  location: { hostname: string; protocol: string; pathname: string; search: string; hash: string },
  appSubdomain: string
): string | null {
  const suffix = cofactorAppHostSuffix(location.hostname);
  if (!suffix || !TENANT_LABEL_PATTERN.test(appSubdomain)) return null;

  const url = new URL(`${location.protocol}//${appSubdomain}.${suffix}`);
  if (url.hostname !== `${appSubdomain}.${suffix}`) return null;

  url.pathname = location.pathname;
  url.search = location.search;
  url.hash = location.hash;
  return url.toString();
}

/**
 * What to do with a `cofactor_org_id` deep-link param once `/me` has loaded.
 * The active org is the session cookie's org, so honoring the param means a
 * server-side switch; membership is checked there, but we only bother the
 * server for orgs `/me` says the viewer can reach.
 *
 * - `none`: no (valid) param, nothing to do
 * - `strip`: already on that org, or not reachable; drop the param
 * - `switch`: ask the API to switch the cookie, then land on the cleaned URL
 */
export function resolveOrgDeepLink(
  href: string,
  user: { orgId: string; organizations?: { id: string }[] }
): { action: 'none' } | { action: 'strip'; cleanUrl: string } | { action: 'switch'; orgId: string; cleanUrl: string } {
  const url = new URL(href);
  const target = url.searchParams.get(ORGANIZATION_CONTEXT_QUERY);
  if (target === null) return { action: 'none' };

  url.searchParams.delete(ORGANIZATION_CONTEXT_QUERY);
  const cleanUrl = url.toString();
  if (!isValidOrganizationId(target)) return { action: 'strip', cleanUrl };

  const id = target.toLowerCase();
  if (id === user.orgId.toLowerCase()) return { action: 'strip', cleanUrl };

  const reachable = user.organizations?.some((org) => org.id.toLowerCase() === id);
  return reachable ? { action: 'switch', orgId: id, cleanUrl } : { action: 'strip', cleanUrl };
}

/**
 * Whether a `?cofactor_org_id=` link that asks the page to start something
 * (`connect=slack`, `connect=github`) means the org this tab is in. `resolveOrgDeepLink`
 * also answers `strip` for an org the viewer can't reach, and starting an install
 * then would connect the wrong org. No param at all means the link names no org.
 */
export function deepLinkTargetsActiveOrg(href: string, user: { orgId: string }): boolean {
  const target = new URL(href).searchParams.get(ORGANIZATION_CONTEXT_QUERY);
  return target === null || target.toLowerCase() === user.orgId.toLowerCase();
}

/** Request header carrying the org this tab rendered; the API refuses with 409 if the cookie org differs. */
export const EXPECTED_ORG_HEADER = 'x-cofactor-expected-org';

/** Error code the API returns (HTTP 409) when the cookie's org is not the one this tab rendered. */
export const ORGANIZATION_CHANGED_CODE = 'ORGANIZATION_CHANGED';

/** sessionStorage key holding the timestamp of the last org-changed reload; survives the reload to show a notice. */
export const ORG_CHANGED_NOTICE_KEY = 'cofactor:orgChangedNotice';

/** A second org-changed signal inside this window is ignored, so a reload can never loop. */
const ORG_CHANGED_RELOAD_GUARD_MS = 5000;

/** Cross-tab channel announcing that this browser's session switched organizations. */
export const ORG_CHANNEL_NAME = 'cofactor-org';

// In memory on purpose: it must describe what THIS tab rendered, so it cannot live in shared storage.
let renderedOrgId: string | null = null;

export function setRenderedOrganizationId(id: string): void {
  renderedOrgId = id;
}

export function getRenderedOrganizationId(): string | null {
  return renderedOrgId;
}

/** Test seam: forget the rendered org. */
export function clearRenderedOrganizationId(): void {
  renderedOrgId = null;
}

/** Paths exempt from the expectation header (mirrors the server exemptions). */
export function isOrgExpectationExempt(pathname: string): boolean {
  return (
    pathname.includes('/auth/') ||
    /\/me\/?$/.test(pathname) ||
    /\/organizations\/[^/]+\/switch\/?$/.test(pathname)
  );
}

/** Flag value is `<timestamp>`, or `<timestamp>:seen` once the notice was shown (the timestamp keeps guarding loops). */
function readNoticeFlag(): { at: number; seen: boolean } | null {
  try {
    const raw = window.sessionStorage.getItem(ORG_CHANGED_NOTICE_KEY);
    if (!raw) return null;
    const [at, seen] = raw.split(':');
    return { at: Number(at), seen: seen === 'seen' };
  } catch {
    return null;
  }
}

/**
 * Record that the org changed under this tab and reload so `/me` re-renders the
 * current route in the cookie org. Returns false (and does nothing) when a
 * reload for this reason already happened in the last few seconds.
 */
export function reloadForOrganizationChange(now: number = Date.now()): boolean {
  const last = readNoticeFlag();
  if (last && now - last.at < ORG_CHANGED_RELOAD_GUARD_MS) return false;
  try {
    window.sessionStorage.setItem(ORG_CHANGED_NOTICE_KEY, String(now));
  } catch {
    // sessionStorage unavailable: reload anyway; the notice just won't show.
  }
  window.location.reload();
  return true;
}

/** True once per reload when the previous page load was triggered by an org change. */
export function consumeOrganizationChangedNotice(now: number = Date.now()): boolean {
  const flag = readNoticeFlag();
  if (!flag || flag.seen || now - flag.at > 30_000) return false;
  try {
    window.sessionStorage.setItem(ORG_CHANGED_NOTICE_KEY, `${flag.at}:seen`);
  } catch {
    // ignore
  }
  return true;
}
